import React, { useState } from 'react'
import { Card } from '../ui/card'
import { Label } from '../ui/label'
import { Checkbox } from '../ui/checkbox'
import { AlertCircle, XCircle, Upload, Check, ChevronsUpDown, Search } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog'
import { Button } from '../ui/button'
import { VisuallyHidden } from '../ui/visually-hidden'
import { BulkUploadModal } from './bulk-upload-modal'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command'
import { addManualRewardPoints, createDefaultManualRewardRequest } from '../../api/rewards'
import { countWords } from '../../lib/utils'

interface AddBonusPointsProps {
  onAddTransaction?: (transaction: Omit<BonusPointTransaction, 'id' | 'date'>) => void
  onAddBulkTransactions?: (transactions: Omit<BonusPointTransaction, 'id' | 'date'>[]) => void
  onNavigateToHistory?: () => void
}

interface BonusPointTransaction {
  id: string
  firstName: string
  lastName: string
  email: string
  employeeEntrataEmail?: string
  points: number
  description: string
  rewardedBy: string
  date: string
  department?: string
}

interface FormData {
  firstName: string
  lastName: string
  email: string
  points: string
  departmentFundingPoints: string
  isEntrataEmployee: boolean
  employeeEntrataEmail: string
  description: string
}

interface FormErrors {
  firstName?: string
  lastName?: string
  email?: string
  points?: string
  departmentFundingPoints?: string
  employeeEntrataEmail?: string
  description?: string
}

const departments = [
  { id: '1', name: 'Administration' },
  { id: '2', name: 'Maintenance' },
  { id: '3', name: 'Leasing' },
  { id: '4', name: 'Marketing' },
  { id: '5', name: 'Customer Service' },
  { id: '6', name: 'Accounting' },
  { id: '7', name: 'Property Management' },
  { id: '8', name: 'Security' },
  { id: '9', name: 'Landscaping' },
  { id: '10', name: 'Housekeeping' },
  { id: '11', name: 'IT Support' },
  { id: '12', name: 'Human Resources' },
  { id: '13', name: 'Legal' },
  { id: '14', name: 'Operations' },
  { id: '15', name: 'Training & Development' },
]

const initialFormData: FormData = {
  firstName: '',
  lastName: '',
  email: '',
  points: '',
  departmentFundingPoints: '',
  isEntrataEmployee: false,
  employeeEntrataEmail: '',
  description: '',
}

export function AddBonusPoints({
  onAddTransaction,
  onAddBulkTransactions,
  onNavigateToHistory,
}: AddBonusPointsProps) {
  const [formData, setFormData] = useState<FormData>(initialFormData)
  const [errors, setErrors] = useState<FormErrors>({})
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [departmentOpen, setDepartmentOpen] = useState(false)
  const [departmentSearchValue, setDepartmentSearchValue] = useState('')
  const [error, setError] = useState<string | null>(null)

  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required'
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required'
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }
    if (!formData.points.trim()) {
      newErrors.points = 'Points value is required'
    } else {
      const pointsValue = parseInt(formData.points)
      if (isNaN(pointsValue) || pointsValue < 1 || pointsValue > 10000) {
        newErrors.points = 'Points must be between 1 and 10000'
      }
    }
    if (!formData.departmentFundingPoints) {
      newErrors.departmentFundingPoints = 'Please select a department'
    }

    if (formData.isEntrataEmployee && !formData.employeeEntrataEmail.trim()) {
      newErrors.employeeEntrataEmail = 'Entrata email is required for employees'
    } else if (formData.isEntrataEmployee && formData.employeeEntrataEmail.trim()) {
      if (!isValidEmail(formData.employeeEntrataEmail)) {
        newErrors.employeeEntrataEmail = 'Please enter a valid email address'
      } else if (!formData.employeeEntrataEmail.endsWith('@entrata.com')) {
        newErrors.employeeEntrataEmail = 'Employee email must be an @entrata.com address'
      }
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Generic handler for text fields that clears errors when user types
  const handleTextFieldChange = (fieldName: keyof FormData) => (value: string) => {
    // Special handling for description field
    if (fieldName === 'description') {
      const wordCount = countWords(value)

      if (wordCount > 500) {
        setErrors(prev => ({ ...prev, description: 'Description must be 500 words or less' }))
        return
      }
      // Clear error if valid
      if (errors.description) {
        setErrors(prev => ({ ...prev, description: undefined }))
      }
    } else {
      // Clear error for other fields if field has value and was previously in error state
      if (errors[fieldName as keyof FormErrors] && value.trim()) {
        setErrors(prev => ({ ...prev, [fieldName]: undefined }))
      }
    }
    setFormData(prev => ({ ...prev, [fieldName]: value }))
  }

  const handlePointsChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '')
    setFormData(prev => ({ ...prev, points: numericValue }))
    if (errors.points && numericValue) {
      setErrors(prev => ({ ...prev, points: undefined }))
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) return

    setIsSubmitting(true)

    try {
      const selectedDept = departments.find(dept => dept.id === formData.departmentFundingPoints)
      const departmentId = selectedDept ? parseInt(selectedDept.id) : 23

      const apiRequest = createDefaultManualRewardRequest(
        formData.firstName,
        formData.lastName,
        formData.email,
        parseInt(formData.points),
        formData.description,
        formData.isEntrataEmployee,
        formData.employeeEntrataEmail,
        departmentId
      )

      const response = await addManualRewardPoints(apiRequest)

      const isSuccess =
        response &&
        (response.success === true ||
          (response.message && response.message.toLowerCase().includes('success')))

      if (isSuccess) {
        setError(null)
        setIsSubmitting(false)
        if (onAddTransaction) {
          onAddTransaction({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            employeeEntrataEmail: formData.employeeEntrataEmail || undefined,
            points: parseInt(formData.points),
            description: formData.description,
            rewardedBy: 'Admin User',
            department: selectedDept?.name,
          })
        }
        setShowSuccessModal(true)
      } else {
        setError('Unable to Award Points')
      }
    } catch {
      setError('Failed to add bonus points')
      setIsSubmitting(false)
    }
  }

  const resetForm = () => {
    setFormData(initialFormData)
    setErrors({})
    setError(null)
  }

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false)
    resetForm()
  }

  const handleViewTransactionHistory = () => {
    handleSuccessModalClose()
    onNavigateToHistory?.()
  }

  const selectedDepartment = departments.find(dept => dept.id === formData.departmentFundingPoints)
  const filteredDepartments = departments.filter(department =>
    department.name.toLowerCase().includes(departmentSearchValue.toLowerCase())
  )

  const renderFieldError = (error?: string) => {
    if (!error) return null
    return (
      <div className="flex items-center gap-2 text-destructive text-sm">
        <AlertCircle className="h-4 w-4" />
        <span>{error}</span>
      </div>
    )
  }

  const renderInputField = (
    id: string,
    label: string,
    value: string,
    onChange: (value: string) => void,
    placeholder: string,
    type: string = 'text',
    required: boolean = false,
    error?: string
  ) => (
    <div className="space-y-2">
      <Label htmlFor={id}>
        {label} {required && <span className="text-destructive">*</span>}
      </Label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        className={`w-full border rounded-md p-2 bg-input-background ${
          error ? 'border-destructive' : 'border-border'
        }`}
        placeholder={placeholder}
      />
      {renderFieldError(error)}
    </div>
  )

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {error && (
            <div className="flex items-center gap-2 p-4 bg-destructive/10 border border-destructive/20 rounded-md">
              <XCircle className="h-5 w-5 text-destructive" />
              <span className="text-destructive text-sm">{error}</span>
            </div>
          )}

          <div className="flex items-center justify-between mb-6">
            <div>
              <h2>Add Bonus Points</h2>
              <p className="text-muted-foreground mt-1"></p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowBulkUploadModal(true)}
              className="flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              Bulk Upload Points
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInputField(
              'firstName',
              'First Name',
              formData.firstName,
              handleTextFieldChange('firstName'),
              'Enter first name',
              'text',
              true,
              errors.firstName
            )}
            {renderInputField(
              'lastName',
              'Last Name',
              formData.lastName,
              handleTextFieldChange('lastName'),
              'Enter last name',
              'text',
              true,
              errors.lastName
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInputField(
              'email',
              'Email Address',
              formData.email,
              handleTextFieldChange('email'),
              'Enter email address',
              'email',
              true,
              errors.email
            )}

            <div className="space-y-2">
              <Label htmlFor="department">
                Department Funding Points <span className="text-destructive">*</span>
              </Label>
              <Popover open={departmentOpen} onOpenChange={setDepartmentOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={departmentOpen}
                    className={`w-full justify-between bg-input-background ${
                      errors.departmentFundingPoints ? 'border-destructive' : 'border-border'
                    }`}
                  >
                    {selectedDepartment ? selectedDepartment.name : 'Select department...'}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0">
                  <Command>
                    <div className="flex h-9 items-center gap-2 px-3">
                      <Search className="size-4 shrink-0 opacity-50" />
                      <CommandInput
                        placeholder="Search departments..."
                        value={departmentSearchValue}
                        onValueChange={setDepartmentSearchValue}
                        className="border-0 focus:ring-0 focus:outline-none shadow-none bg-transparent"
                      />
                    </div>
                    <CommandList>
                      <CommandEmpty>No department found.</CommandEmpty>
                      <CommandGroup>
                        {filteredDepartments.map(department => (
                          <CommandItem
                            key={department.id}
                            value={department.name}
                            onSelect={() => {
                              setFormData(prev => ({
                                ...prev,
                                departmentFundingPoints: department.id,
                              }))
                              setDepartmentOpen(false)
                              setDepartmentSearchValue('')
                              if (errors.departmentFundingPoints) {
                                setErrors(prev => ({ ...prev, departmentFundingPoints: undefined }))
                              }
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                formData.departmentFundingPoints === department.id
                                  ? 'opacity-100'
                                  : 'opacity-0'
                              }`}
                            />
                            {department.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {renderFieldError(errors.departmentFundingPoints)}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {renderInputField(
              'points',
              'Points',
              formData.points,
              handlePointsChange,
              'Enter points value (numbers only)',
              'text',
              true,
              errors.points
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="isEntrataEmployee"
                checked={formData.isEntrataEmployee}
                onCheckedChange={checked => {
                  setFormData(prev => ({
                    ...prev,
                    isEntrataEmployee: !!checked,
                    employeeEntrataEmail: checked ? prev.employeeEntrataEmail : '',
                  }))
                  if (!checked && errors.employeeEntrataEmail) {
                    setErrors(prev => ({ ...prev, employeeEntrataEmail: undefined }))
                  }
                }}
              />
              <Label htmlFor="isEntrataEmployee" className="text-sm">
                Is Entrata Employee?
              </Label>
            </div>

            {formData.isEntrataEmployee && (
              <div className="space-y-2">
                <Label htmlFor="employeeEntrataEmail">
                  Employee Entrata Email <span className="text-destructive">*</span>
                </Label>
                <input
                  id="employeeEntrataEmail"
                  type="email"
                  value={formData.employeeEntrataEmail}
                  onChange={e => {
                    const value = e.target.value
                    setFormData(prev => ({ ...prev, employeeEntrataEmail: value }))

                    if (
                      errors.employeeEntrataEmail &&
                      value.trim() &&
                      isValidEmail(value) &&
                      value.endsWith('@entrata.com')
                    ) {
                      setErrors(prev => ({ ...prev, employeeEntrataEmail: undefined }))
                    }
                  }}
                  className={`w-full border rounded-md p-2 bg-input-background ${
                    errors.employeeEntrataEmail ? 'border-destructive' : 'border-border'
                  }`}
                  placeholder="Enter @entrata.com email address"
                />
                {renderFieldError(errors.employeeEntrataEmail)}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-destructive">*</span>
            </Label>
            <textarea
              id="description"
              value={formData.description}
              onChange={e => handleTextFieldChange('description')(e.target.value)}
              className={`w-full border rounded-md p-2 bg-input-background min-h-20 resize-none ${
                errors.description ? 'border-destructive' : 'border-border'
              }`}
              placeholder="Enter a description for the bonus points"
              rows={3}
            />
            <div className="flex items-center justify-between">
              {renderFieldError(errors.description)}
              <div className="text-sm text-muted-foreground ml-auto">
                {countWords(formData.description)}/500 words
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting ? 'Awarding Points...' : 'Award Bonus Points'}
            </Button>
          </div>
        </form>
      </Card>

      <Dialog open={showSuccessModal} onOpenChange={handleSuccessModalClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <Check className="h-5 w-5" />
              Points Awarded Successfully
            </DialogTitle>
            <VisuallyHidden>
              <DialogDescription>Success confirmation for bonus points award</DialogDescription>
            </VisuallyHidden>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-center">
              You've successfully rewarded{' '}
              <strong>{formData.points} Homebody Rewards points</strong> to{' '}
              <strong>
                {formData.firstName} {formData.lastName}
              </strong>
              . A confirmation email will be sent to <strong>{formData.email}</strong>.
            </p>
            <div className="flex justify-center">
              <Button onClick={handleViewTransactionHistory} className="px-6">
                View Transaction History
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showBulkUploadModal && (
        <BulkUploadModal
          isOpen={showBulkUploadModal}
          onClose={() => setShowBulkUploadModal(false)}
          onBulkSuccess={onAddBulkTransactions || (() => {})}
          onNavigateToHistory={onNavigateToHistory || (() => {})}
        />
      )}
    </div>
  )
}
